import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, DownloadSimple, Sparkle, FileText, PaperPlaneTilt, LinkSimple, Eye, PencilSimple, Trash } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import InvoiceQuickViewDialog from "@/components/InvoiceQuickViewDialog";
import InvoiceEditDialog from "@/components/InvoiceEditDialog";

const fmt$ = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function lastFullWeek() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const mondayOffset = day === 0 ? 6 : day - 1;
  const lastSunday = new Date(d); lastSunday.setDate(d.getDate() - mondayOffset - 1);
  const lastMonday = new Date(lastSunday); lastMonday.setDate(lastSunday.getDate() - 6);
  return { start: lastMonday.toISOString().slice(0, 10), end: lastSunday.toISOString().slice(0, 10) };
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wk = lastFullWeek();
  const [form, setForm] = useState({ engagement_id: "", period_start: wk.start, period_end: wk.end, mode: "single" });
  const [sendInv, setSendInv] = useState(null);
  const [sendForm, setSendForm] = useState({ recipient_email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  // Iter 85.66 · Quick View + Edit surfaces. Both are scoped to a single
  // invoice at a time; opening Edit from the QuickView chevron closes the
  // preview so the advisor edits against a clean canvas, then re-opens
  // the preview after save so the review-and-send loop remains one modal
  // deep at any moment.
  const [previewInv, setPreviewInv] = useState(null);
  const [editInv, setEditInv] = useState(null);

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data));
    api.get("/engagements").then((r) => setEngagements(r.data));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const payload = form.mode === "all"
        ? { include_all_open_engagements: true, period_start: form.period_start, period_end: form.period_end }
        : { engagement_id: form.engagement_id, period_start: form.period_start, period_end: form.period_end };
      if (form.mode === "single" && !form.engagement_id) {
        setBusy(false);
        return toast.error("Select an engagement");
      }
      const { data } = await api.post("/invoices/generate", payload);
      if (data.count === 0) toast.error("No billable timesheets in this period");
      else toast.success(`Generated ${data.count} invoice${data.count > 1 ? "s" : ""}`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const download = (inv) => {
    if (!inv.pdf_document_id) return toast.error("No PDF");
    // Auth cookie rides on the top-level <a> click (same-site), so no query token needed.
    const url = `${API}/documents/${inv.pdf_document_id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.invoice_number}.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Iter 85.71 · Draft-only delete. Confirmation required. Sent/Paid rows
  // are rejected on the backend to preserve accounting history — the UI
  // simply hides the trash icon for those statuses so partners aren't
  // teased with a control they can't use.
  const deleteInvoice = async (inv) => {
    if ((inv.status || "").toLowerCase() !== "draft") {
      toast.error("Only Draft invoices can be deleted. Void a sent invoice to preserve history.");
      return;
    }
    if (!confirm(`Delete draft invoice ${inv.invoice_number}? This removes the row and its draft PDF. Timesheet entries stay intact so you can re-invoice under a new number.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Deleted draft ${inv.invoice_number}`);
      // Close any open modal referencing this invoice so the workspace
      // refresh doesn't leave a stale preview visible.
      if (previewInv?.id === inv.id) setPreviewInv(null);
      if (editInv?.id === inv.id) setEditInv(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const updateStatus = async (inv, status) => {
    await api.put(`/invoices/${inv.id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const openSend = (inv) => {
    setSendInv(inv);
    setSendForm({
      recipient_email: inv.sent_to || "",
      subject: `Invoice ${inv.invoice_number}${inv.client_name ? " · " + inv.client_name : ""}`,
      message: "",
    });
  };

  const sendInvoice = async () => {
    if (!sendInv) return;
    if (!sendForm.recipient_email) return toast.error("Recipient email required");
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${sendInv.id}/send`, {
        recipient_email: sendForm.recipient_email,
        subject: sendForm.subject || undefined,
        message: sendForm.message || undefined,
      });
      toast.success(`Invoice sent to ${sendForm.recipient_email}`);
      setSendInv(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const copyShareLink = async (inv) => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}/share-link`);
      await navigator.clipboard.writeText(data.view_url);
      toast.success(`Public view link copied · valid ${data.expires_in_days} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    }
  };

  const totalDraft = invoices.filter(i => i.status === "draft").reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);

  return (
    <PageShell size="default">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Cash Cycle</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1 flex gap-6">
            <span>Draft <span className="text-primary font-mono data-cell ml-1">{fmt$(totalDraft)}</span></span>
            <span>Paid <span className="text-primary font-mono data-cell ml-1">{fmt$(totalPaid)}</span></span>
            <span>{invoices.length} total</span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="generate-invoice-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
              <Sparkle size={14} weight="fill" className="mr-2" /> Draft invoice{form.mode === "all" ? "s" : ""}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-card">
            <DialogHeader><DialogTitle className="font-display">Draft weekly invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single engagement</SelectItem>
                    <SelectItem value="all">All active time & materials / retainer engagements</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.mode === "single" && (
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Engagement</Label>
                  <Select value={form.engagement_id} onValueChange={(v) => setForm({ ...form, engagement_id: v })}>
                    <SelectTrigger data-testid="invoice-engagement-select" className="rounded-none bg-secondary/40 mt-1"><SelectValue placeholder="Select engagement" /></SelectTrigger>
                    <SelectContent>
                      {engagements.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Period start</Label>
                  <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
                </div>
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Period end</Label>
                  <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
                </div>
              </div>
              <p className="text-[0.6875rem] text-muted-foreground">
                YadidAi compiles a line-item PDF from billable timesheets in this period. Rate defaults to entry rate or firm default ($250/hr). Firm letterhead pulls from Settings.
              </p>
            </div>
            <DialogFooter>
              <Button data-testid="invoice-submit" onClick={generate} disabled={busy} className="rounded-none bg-primary text-primary-foreground">
                {busy ? "Drafting…" : "Generate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left px-5 py-3 font-normal">Invoice</th>
              <th className="text-left px-3 py-3 font-normal">Partner Organization</th>
              <th className="text-left px-3 py-3 font-normal">Engagement</th>
              <th className="text-left px-3 py-3 font-normal">Period</th>
              <th className="text-right px-3 py-3 font-normal">Total</th>
              <th className="text-left px-3 py-3 font-normal">Status</th>
              <th className="text-right px-5 py-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan="7" className="px-5 py-12 text-center text-xs text-muted-foreground">No invoices yet. Draft your first weekly invoice.</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-5 py-3">
                  <div className="font-mono data-cell text-sm">{inv.invoice_number}</div>
                  <div className="text-[0.625rem] font-mono text-muted-foreground">{new Date(inv.issue_date).toLocaleDateString()}</div>
                </td>
                <td className="px-3 py-3 text-xs">{inv.client_name}</td>
                <td className="px-3 py-3 text-xs">{inv.engagement_name || "—"}</td>
                <td className="px-3 py-3 text-xs data-cell">{inv.period_start} → {inv.period_end}</td>
                <td className="px-3 py-3 text-right data-cell text-primary font-medium">{fmt$(inv.total)}</td>
                <td className="px-3 py-3">
                  <Select value={inv.status} onValueChange={(v) => updateStatus(inv, v)}>
                    <SelectTrigger className="rounded-none bg-transparent border-0 p-0 h-auto text-xs uppercase font-mono tracking-widest" data-testid={`invoice-status-${inv.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["draft","sent","paid","void"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => setPreviewInv(inv)}
                    className="text-muted-foreground hover:text-primary p-1 mr-1"
                    title="Quick view"
                    data-testid={`quickview-invoice-${inv.id}`}
                  >
                    <Eye size={16} />
                  </button>
                  {inv.status === "draft" && (
                    <button
                      onClick={() => setEditInv(inv)}
                      className="text-muted-foreground hover:text-primary p-1 mr-1"
                      title="Edit invoice"
                      data-testid={`edit-invoice-${inv.id}`}
                    >
                      <PencilSimple size={16} />
                    </button>
                  )}
                  <button onClick={() => copyShareLink(inv)} className="text-muted-foreground hover:text-primary p-1 mr-1" title="Copy public view link" data-testid={`share-invoice-${inv.id}`}>
                    <LinkSimple size={16} />
                  </button>
                  <button onClick={() => openSend(inv)} className="text-muted-foreground hover:text-primary p-1 mr-1" title="Send invoice by email" data-testid={`send-invoice-${inv.id}`}>
                    <PaperPlaneTilt size={16} weight={inv.sent_at ? "fill" : "regular"} />
                  </button>
                  <button onClick={() => download(inv)} className="text-muted-foreground hover:text-primary p-1" title="Download PDF" data-testid={`download-invoice-${inv.id}`}>
                    <DownloadSimple size={16} />
                  </button>
                  {inv.status === "draft" && (
                    <button
                      onClick={() => deleteInvoice(inv)}
                      className="text-muted-foreground hover:text-destructive p-1 ml-1"
                      title="Delete draft invoice"
                      data-testid={`delete-invoice-${inv.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sendInv} onOpenChange={(v) => { if (!v) setSendInv(null); }}>
        <DialogContent className="rounded-none bg-card" data-testid="send-invoice-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><PaperPlaneTilt size={16} weight="fill" className="text-primary" /> Send invoice {sendInv?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Recipient email</Label>
              <Input data-testid="send-recipient" type="email" value={sendForm.recipient_email} onChange={(e) => setSendForm({ ...sendForm, recipient_email: e.target.value })} placeholder="cfo@client.com" className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Subject (optional)</Label>
              <Input value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} placeholder="Auto-generated if blank" className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <div>
              <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Personal note (optional)</Label>
              <Textarea data-testid="send-message" rows={4} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} placeholder="Add a personal note that will appear above the invoice details in the email…" className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <p className="text-[0.6875rem] text-muted-foreground">The PDF will be attached and a 7-day secure download link included. Once sent, invoice status auto-moves to 'sent'.</p>
          </div>
          <DialogFooter>
            <Button data-testid="send-invoice-submit" onClick={sendInvoice} disabled={sending} className="rounded-none bg-primary text-primary-foreground">
              {sending ? "Sending…" : "Send invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <InvoiceQuickViewDialog
        invoice={previewInv}
        open={!!previewInv}
        onOpenChange={(v) => { if (!v) setPreviewInv(null); }}
        onDownload={(inv) => download(inv)}
        onSend={(inv) => { setPreviewInv(null); openSend(inv); }}
        onEdit={(inv) => { setPreviewInv(null); setEditInv(inv); }}
        onDelete={(inv) => deleteInvoice(inv)}
      />
      <InvoiceEditDialog
        invoice={editInv}
        open={!!editInv}
        onOpenChange={(v) => { if (!v) setEditInv(null); }}
        onSaved={(fresh) => {
          setEditInv(null);
          load();
          // Re-open the preview against the freshly-persisted invoice so
          // the partner sees their edits immediately in the client-facing
          // sheet — closing the loop for Draft → Quick View → Edit →
          // Review → Send.
          setPreviewInv(fresh);
        }}
      />
    </PageShell>
  );
}
